import { createContext, useContext, useId, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';

/**
 * LE KIT PARAMETRES : la seule voie pour afficher quelque chose dans le hub
 * Parametres, panneaux du socle comme reglages d'apps. Chaque brique se montre
 * dans l'onglet « Gabarit paramètres » (pile de dev, `ParametresTemplatePanel`
 * du shell) : un besoin qui n'y entre pas s'ajoute d'abord ICI et au gabarit,
 * puis s'utilise. Le test `parametres-kit` du shell refuse le reste.
 *
 * Ce fichier : le cadre et les lignes. Les controles (champ, menu, fichier,
 * couleur, icones) vivent dans `SettingsControls.tsx`, les tableaux dans
 * `SettingsTables.tsx`.
 *
 * <SettingsPanel>
 *   <SettingsSection title="Affichage" contextLabel="Organisation">
 *     <SettingsRow label="Ouvrir sur"><SettingsMenu ... /></SettingsRow>
 *   </SettingsSection>
 * </SettingsPanel>
 */

/** Porte reelle choisie dans les parametres ; null pour les reglages de l'app. */
export interface SettingsDoor {
  screenId: string;
  doorId: string;
}
const SettingsDoorContext = createContext<SettingsDoor | null>(null);
export const SettingsDoorProvider = SettingsDoorContext.Provider;
export const useSettingsDoor = (): SettingsDoor | null => useContext(SettingsDoorContext);

export interface SettingsPanelProps {
  children: ReactNode;
}

/** Le cadre de tous les panneaux : la colonne bornee a 42rem, posee une fois. */
export function SettingsPanel({ children }: SettingsPanelProps): JSX.Element {
  return <div className="max-w-[42rem] px-6 pb-6 pt-0 font-sans empty:hidden">{children}</div>;
}

export interface SettingsSectionProps {
  /** Titre souligne d'un filet. Vide : la section ne porte que ses lignes. */
  title: string;
  /** Portee, a droite du titre (« Organisation », « 3 / 6 épinglées »). */
  contextLabel?: string;
  /** Section verrouillee : cadenas a droite du titre, raison au survol. */
  lock?: string;
  /** Ancre de la section. */
  id?: string;
  children: ReactNode;
}

/** Groupe de reglages : titre, portee a droite, filet, puis les lignes. */
export function SettingsSection({ title, contextLabel, lock, id, children }: SettingsSectionProps): JSX.Element {
  if (!title) return <section id={id} className="font-sans">{children}</section>;
  return (
    <section id={id} className="mt-6 scroll-mt-20 font-sans">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border pb-2.5">
        <h3 className="text-base font-semibold leading-normal text-ink-900">{title}</h3>
        {contextLabel != null || lock != null ? (
          <span className="flex items-center gap-2 text-xs text-ink-400">
            {contextLabel}
            {lock != null ? <SettingsLock label={lock} /> : null}
          </span>
        ) : null}
      </header>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

export interface SettingsGroupProps {
  /** Legende integree au contour. */
  title: string;
  children: ReactNode;
}

/** Sous-groupe d'une section : contour fin, legende integree. */
export function SettingsGroup({ title, children }: SettingsGroupProps): JSX.Element {
  return (
    <div className="relative mt-5 rounded-sm border border-border px-3 pb-1 pt-2">
      <h4 className="-mt-[1.125rem] mb-1 w-fit bg-surface px-2 text-[0.8125rem] font-semibold text-ink-900">{title}</h4>
      {children}
    </div>
  );
}

export interface SettingsRowProps {
  label: string;
  /** Une ligne d'aide, seulement si indispensable. */
  hint?: string;
  /** Etat du reglage contre le libelle (« Lecture seule »). */
  badge?: string;
  /** Reglage verrouille : cadenas avant le controle, raison au survol. */
  lock?: string;
  /** Le controle, aligne a droite ; un champ ou un menu prend toute la colonne. */
  children: ReactNode;
}

/** Ligne de reglage : libelle a gauche, controle a droite. */
export function SettingsRow({ label, hint, badge, lock, children }: SettingsRowProps): JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(7rem,11rem)_minmax(0,1fr)] items-center gap-x-6 gap-y-2 py-3.5 font-sans">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-2 text-sm font-medium leading-[1.4] text-ink-700">
          {label}
          {badge != null && (
            <span className="rounded-xs bg-surface-3 px-1.5 py-[0.0625rem] text-[0.6875rem] font-medium text-ink-500">
              {badge}
            </span>
          )}
        </span>
        {hint != null && hint !== '' && (
          <span className="max-w-[26rem] text-pretty text-[0.8125rem] leading-[1.45] text-ink-500">{hint}</span>
        )}
      </div>
      <div data-settings-control className="flex min-w-0 flex-wrap items-center justify-end gap-2">
        {lock != null ? <SettingsLock label={lock} /> : null}
        {children}
      </div>
    </div>
  );
}

/** Cadenas gris, raison au survol et au focus. */
export function SettingsLock({ label = 'Administrateurs uniquement' }: { label?: string }): JSX.Element {
  const id = useId();
  return (
    <button
      type="button"
      aria-label={label}
      aria-describedby={id}
      className="group relative flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border"
    >
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x={5} y={10} width={14} height={11} rx={2} /><path d="M8 10V7a4 4 0 0 1 8 0v3" /><path d="M12 14v3" />
      </svg>
      <span
        id={id}
        role="tooltip"
        className="pointer-events-none absolute right-0 top-full z-20 mt-1 w-max max-w-64 rounded-sm border border-border bg-surface px-2 py-1 text-xs font-normal text-ink-500 opacity-0 shadow-pop group-hover:opacity-100 group-focus-visible:opacity-100"
      >
        {label}
      </span>
    </button>
  );
}

/** Encadre gris d'information, icone en tete. Seulement si vraiment utile. */
export function SettingsInfo({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p className="mt-2.5 flex items-center gap-2 rounded-sm border border-border bg-surface-1 px-3 py-2 text-[0.8125rem] leading-[1.45] text-ink-500">
      <svg viewBox="0 0 240 240" width={15} height={15} fill="currentColor" className="shrink-0 text-ink-400" aria-hidden="true">
          <path d="M0 0 C1.55 1.3 3.09 2.62 4.62 3.94 C5.36 4.53 6.09 5.12 6.85 5.72 C28.48 23.69 42.77 53.13 45.62 80.94 C47.33 115.99 39.88 146.94 16.62 173.94 C15.74 175.04 15.74 175.04 14.84 176.16 C-3.13 197.79 -32.56 212.08 -60.38 214.94 C-95.42 216.65 -126.37 209.2 -153.38 185.94 C-154.11 185.35 -154.84 184.76 -155.6 184.15 C-177.23 166.18 -191.52 136.75 -194.38 108.94 C-196.08 73.89 -188.63 42.94 -165.38 15.94 C-164.49 14.84 -164.49 14.84 -163.59 13.71 C-123.74 -34.26 -47.51 -37.24 0 0 Z M-137.38 23.94 C-138.26 24.74 -139.15 25.53 -140.07 26.36 C-158.93 44.14 -168.54 67.79 -169.37 93.58 C-169.66 117.07 -161.38 140.56 -145.38 157.94 C-144.58 158.83 -143.78 159.72 -142.96 160.63 C-125.18 179.49 -101.51 189.12 -75.73 189.93 C-52.27 190.21 -28.8 181.77 -11.38 165.94 C-10.04 164.74 -10.04 164.74 -8.67 163.52 C10.17 145.73 19.8 122.08 20.62 96.29 C20.9 72.83 12.46 49.37 -3.38 31.94 C-4.57 30.6 -4.57 30.6 -5.8 29.23 C-23.59 10.39 -47.22 0.78 -73.02 -0.06 C-96.51 -0.35 -119.99 7.93 -137.38 23.94 Z " transform="translate(194.375,25.0625)" />
          <path d="M0 0 C17.49 0 34.98 0 53 0 C53 21.45 53 42.9 53 65 C61.91 65 70.82 65 80 65 C80 73.58 80 82.16 80 91 C53.6 91 27.2 91 0 91 C0 82.42 0 73.84 0 65 C8.91 65 17.82 65 27 65 C27 52.13 27 39.26 27 26 C18.09 26 9.18 26 0 26 C0 17.42 0 8.84 0 0 Z " transform="translate(80,92)" />
          <path d="M0 0 C8.58 0 17.16 0 26 0 C26 8.25 26 16.5 26 25 C17.42 25 8.84 25 0 25 C0 16.75 0 8.5 0 0 Z " transform="translate(107,50)" />
        </svg>
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/** Ce qui manque : « Aucune liste. », « Aucun raccourci. ». */
export function SettingsEmpty({ children }: { children: ReactNode }): JSX.Element {
  return <p className="py-4 text-[0.8125rem] text-ink-400">{children}</p>;
}

export interface SettingsMessageProps {
  /** `error` : annonce immediate en rouge ; `status` : compte rendu discret. */
  tone?: 'status' | 'error';
  /** Aligne a droite, sous une rangee d'actions. */
  end?: boolean;
  children: ReactNode;
}

/** Le retour d'un geste : erreur ou compte rendu, une ligne. */
export function SettingsMessage({ tone = 'status', end = false, children }: SettingsMessageProps): JSX.Element {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('mt-2 text-xs', tone === 'error' ? 'text-danger' : 'text-ink-500', end && 'text-right')}
    >
      {children}
    </p>
  );
}

/** Rangee de boutons, alignee a droite (Enregistrer, Annuler, Réinitialiser). */
export function SettingsActions({ children }: { children: ReactNode }): JSX.Element {
  return <div className="mt-4 flex flex-wrap items-center justify-end gap-2">{children}</div>;
}

/** Les deux mots d'un enregistrement, ecrits une fois pour tout le kit. */
export const MOTS_ENREGISTREMENT = { enCours: 'Enregistrement…', fait: 'Enregistré' } as const;

export interface SettingsSaveProps {
  onSave: () => void;
  /** L'ecriture en cours : bouton occupe, « Enregistrement… » a sa gauche. */
  busy?: boolean;
  /** L'ecriture faite : « Enregistré » a sa gauche, jusqu'au geste suivant. */
  done?: boolean;
  disabled?: boolean;
  /** Libelle du bouton. Defaut : « Enregistrer ». */
  label?: string;
  variant?: 'primary' | 'secondary';
}

/**
 * LE BOUTON ENREGISTRER DU KIT, a poser dans `SettingsActions`. Une ecriture
 * qui attend le serveur se VOIT (retour du fondateur du 14/10/2026 : l'attente
 * muette faisait croire a un bug) : le bouton tourne et s'eteint, « Enregistrement… »
 * s'ecrit a sa gauche, puis « Enregistré » quand c'est fait. La ligne d'etat est
 * toujours rendue (vide au repos) : un lecteur d'ecran annonce son changement.
 */
export function SettingsSave({
  onSave,
  busy = false,
  done = false,
  disabled = false,
  label = 'Enregistrer',
  variant = 'primary',
}: SettingsSaveProps): JSX.Element {
  const etat = busy ? MOTS_ENREGISTREMENT.enCours : done ? MOTS_ENREGISTREMENT.fait : '';
  return (
    <>
      <span role="status" aria-live="polite" className="text-xs text-ink-500">{etat}</span>
      <Button variant={variant} size="sm" loading={busy} disabled={disabled} onClick={onSave}>{label}</Button>
    </>
  );
}

/** Valeur a recopier telle quelle (adresse, code d'appairage) : police mono. */
export function SettingsCode({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p className="w-full min-w-0 select-all break-all rounded-sm border border-border bg-surface-1 px-3 py-1.5 font-mono text-[0.8125rem] text-ink-900">
      {children}
    </p>
  );
}

export interface SettingsConfirmProps {
  /** Le compte, en une ligne (« 7 fiches à changer »). */
  children: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  /** Libelle du bouton de confirmation. Defaut : « Confirmer ». */
  confirmLabel?: string;
  /** Pendant l'application : boutons eteints, Confirmer en attente. */
  busy?: boolean;
  /** Annuler eteint (une application a reprendre ne s'abandonne pas). */
  cancelDisabled?: boolean;
}

/** Confirmation en une ligne : le compte a gauche, Annuler et Confirmer a droite. */
export function SettingsConfirm({
  children,
  onCancel,
  onConfirm,
  confirmLabel = 'Confirmer',
  busy = false,
  cancelDisabled = false,
}: SettingsConfirmProps): JSX.Element {
  return (
    <div role="alertdialog" className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-sm border border-border bg-surface-1 px-3 py-2">
      <span className="min-w-0 text-[0.8125rem] font-medium text-ink-900">{children}</span>
      <span className="flex shrink-0 items-center gap-2">
        {/* Pendant l'application, le meme mot que `SettingsSave`. */}
        <span role="status" aria-live="polite" className="text-xs text-ink-500">{busy ? MOTS_ENREGISTREMENT.enCours : ''}</span>
        <Button variant="ghost" size="sm" disabled={busy || cancelDisabled} onClick={onCancel}>Annuler</Button>
        <Button variant="primary" size="sm" loading={busy} onClick={onConfirm}>{confirmLabel}</Button>
      </span>
    </div>
  );
}

export interface SettingsCopyTextProps {
  /** La valeur, montree en texte et copiee telle quelle. */
  value: string;
  /** Libelle accessible du bouton (« Copier l’adresse MCP »). */
  label: string;
}

/** Valeur de reglage en texte, avec le geste de copie. */
export function SettingsCopyText({ value, label }: SettingsCopyTextProps): JSX.Element {
  const [copie, setCopie] = useState(false);
  const copier = (): void => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopie(true);
      window.setTimeout(() => setCopie(false), 1500);
    });
  };
  return (
    <span className="flex min-w-0 items-center justify-end gap-2">
      <span className="min-w-0 break-all text-right text-sm text-ink-900">{value}</span>
      <Button variant="secondary" size="sm" aria-label={label} onClick={copier}>{copie ? 'Copié' : 'Copier'}</Button>
    </span>
  );
}
